import takeRight from 'lodash-es/takeRight';

export const getTopLevelDomain = (url: string): null | string => {
  try {
    const matches = url.match(/:\/\/(www[0-9]?\.)?(.[^/:]+)/i);
    const domain = matches && matches[2];
    if (!domain) return null;
    const splitDomain = domain && domain.split('.');
    const topLevel = takeRight(splitDomain, 2);
    return topLevel.join('.');
  } catch (error) {
    return null;
  }
};

/**
 * Parses URL search parameters into an object.
 * @param search - The URL search string.
 * @param options - Options for parsing the search parameters.
 * @returns An object containing the parsed search parameters.
 */
export const parseUrlSearchParams = (
  search: string,
  options?: { decode?: boolean },
): Record<string, string | string[]> => {
  const params = new URLSearchParams(search);

  const decodingFunction = options?.decode ? decodeURIComponent : (value: string) => value;
  const arrayRegex = /\[\d+\]$/;

  return Array.from(params.entries()).reduce<Record<string, string | string[]>>(
    (acc, [key, value]) => {
      if (key.match(arrayRegex)) {
        const arrayKey = key.replace(arrayRegex, '');
        acc[arrayKey] = [...(acc[arrayKey] || []), decodingFunction(value)];
      } else {
        acc[key] = decodingFunction(value);
      }
      return acc;
    },
    {},
  );
};

export const stringifyUrlSearchParams = (
  params: Record<
    string,
    | (Record<string, unknown> | boolean | null | number | string | unknown[])[]
    | Record<string, unknown>
    | boolean
    | null
    | number
    | string
    | undefined
    | unknown[]
  >,
  options?: { addQueryPrefix?: boolean; arrayFormat?: 'brackets' | 'comma' | 'indices' | 'repeat' },
): string => {
  const searchParams = new URLSearchParams();

  const appendParam = (key: string, value: unknown) => {
    if (value === undefined || value === null) return;

    if (typeof value === 'object' && !Array.isArray(value)) {
      Object.keys(value).forEach((nestedKey) => {
        appendParam(`${key}[${nestedKey}]`, (value as Record<string, unknown>)[nestedKey]);
      });
    } else if (Array.isArray(value)) {
      switch (options?.arrayFormat) {
        case 'brackets':
          value.forEach((val) => appendParam(`${key}[]`, val));
          break;
        case 'repeat':
          value.forEach((val) => appendParam(key, val));
          break;
        case 'comma':
          searchParams.append(key, value.join(','));
          break;
        case 'indices':
        default:
          value.forEach((val, index) => appendParam(`${key}[${index}]`, val));
          break;
      }
    } else {
      searchParams.append(key, String(value));
    }
  };

  Object.keys(params).forEach((key) => {
    appendParam(key, params[key]);
  });

  let queryString = searchParams.toString();

  if (options?.addQueryPrefix) queryString = `?${queryString}`;

  return queryString;
};
